import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';

// ─── Tipler ──────────────────────────────────────────────────────────────────

interface Proje { projeID: number; projeAdi: string; }
interface Sozlesme {
    sozlesmeID: number;
    sozlesmeTakipNo: string;
    sozlesmeTarihi: string;
    sozlesmeFiyati: number;
    sozlesmeDurumu: string;
    cari?: { cariID: number; adiSoyadi: string; tc?: string; telefon1?: string; email?: string; adres?: string; };
    daire?: { daireID: number; daireNo: string; tip?: string; brutMetrekare?: number; netMetrekare?: number; kat?: number; blok?: { blokAdi: string; proje?: { projeID: number; projeAdi: string; }; }; };
}
interface Taksit {
    taksitID: number;
    sozlesmeID: number;
    sozlesmeTakipNo?: string;
    vadeTarihi?: string;
    tahsilatTarihi?: string;
    miktar: number;
    odemeDurumu: string;
    odemeTuru?: string;
    aciklama?: string;
}

const EXCEL_SECENEK_LIST = [
    { key: 'musteriler',       label: 'Müşteriler',           icon: 'fas fa-users',              aciklama: 'Tüm müşteri kayıtları ve iletişim bilgileri' },
    { key: 'tedarikciler',     label: 'Tedarikçiler',         icon: 'fas fa-truck',              aciklama: 'Tedarikçi firma ve iletişim kayıtları' },
    { key: 'daireler',         label: 'Daireler',             icon: 'fas fa-door-open',          aciklama: 'Tüm daire stok bilgileri' },
    { key: 'sozlesmeler',      label: 'Sözleşmeler',          icon: 'fas fa-file-contract',      aciklama: 'Tüm satış sözleşmeleri' },
    { key: 'odemeler',         label: 'Ödemeler',             icon: 'fas fa-money-check-alt',    aciklama: 'Bekleyen, ödenen ve tüm taksitler' },
    { key: 'potansiyeller',    label: 'Potansiyel Müşteriler',icon: 'fas fa-user-tag',           aciklama: 'Lead / potansiyel müşteri havuzu' },
    { key: 'musteriNotlari',   label: 'Müşteri Notları',      icon: 'fas fa-sticky-note',        aciklama: 'Müşterilere ait notlar ve görüşme kayıtları' },
    { key: 'personel',         label: 'Personel',             icon: 'fas fa-id-badge',           aciklama: 'Sistem kullanıcıları ve personel listesi' },
];

// ─── Yardımcılar ─────────────────────────────────────────────────────────────

const tarihFormat = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString('tr-TR') : '';

const paraFormat = (n?: number | null) =>
    typeof n === 'number' ? n.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : '';

// ─── Component ───────────────────────────────────────────────────────────────

const DisaAktar: React.FC = () => {
    const { user } = useAuth();

    // Excel
    const [secili, setSecili] = useState<Set<string>>(new Set());
    const [excelYukleniyor, setExcelYukleniyor] = useState(false);
    const [excelMesaj, setExcelMesaj] = useState<{ tip: 'ok' | 'hata'; mesaj: string } | null>(null);

    // PDF
    const [projeler, setProjeler] = useState<Proje[]>([]);
    const [seciliProjeler, setSeciliProjeler] = useState<Set<number>>(new Set());
    const [pdfYukleniyor, setPdfYukleniyor] = useState(false);
    const [pdfMesaj, setPdfMesaj] = useState<{ tip: 'ok' | 'hata'; mesaj: string } | null>(null);
    const [projelerYukleniyor, setProjelerYukleniyor] = useState(false);

    const mesajGoster = (setter: typeof setExcelMesaj, tip: 'ok' | 'hata', mesaj: string) => {
        setter({ tip, mesaj });
        setTimeout(() => setter(null), 4000);
    };

    // ── Proje listesini yükle ─────────────────────────────────────────────────

    const projeleriYukle = useCallback(async () => {
        setProjelerYukleniyor(true);
        try {
            const res = await axiosInstance.get('/api/Projeler');
            setProjeler(res.data || []);
        } catch {
            setProjeler([]);
        } finally {
            setProjelerYukleniyor(false);
        }
    }, []);

    useEffect(() => { projeleriYukle(); }, [projeleriYukle]);

    // ── Excel dışa aktar ─────────────────────────────────────────────────────

    const toggleSecim = (key: string) => {
        setSecili(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const tumunuSec = () => {
        if (secili.size === EXCEL_SECENEK_LIST.length) setSecili(new Set());
        else setSecili(new Set(EXCEL_SECENEK_LIST.map(s => s.key)));
    };

    const excelIndir = async () => {
        if (secili.size === 0) { mesajGoster(setExcelMesaj, 'hata', 'En az bir kategori seçin.'); return; }
        setExcelYukleniyor(true);
        try {
            const wb = XLSX.utils.book_new();
            const tasks = Array.from(secili).map(key => fetchVeEkle(wb, key));
            await Promise.all(tasks);
            const tarih = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
            XLSX.writeFile(wb, `YapiCRM_Disa_Aktar_${tarih}.xlsx`);
            mesajGoster(setExcelMesaj, 'ok', `${secili.size} kategori başarıyla dışa aktarıldı.`);
        } catch (e: any) {
            mesajGoster(setExcelMesaj, 'hata', 'Dışa aktarım sırasında hata oluştu.');
        } finally {
            setExcelYukleniyor(false);
        }
    };

    const fetchVeEkle = async (wb: XLSX.WorkBook, key: string) => {
        const secenek = EXCEL_SECENEK_LIST.find(s => s.key === key)!;
        let rows: Record<string, any>[] = [];

        if (key === 'musteriler') {
            const res = await axiosInstance.get('/api/Cariler?tur=Müşteri');
            rows = (res.data || []).map((c: any) => ({
                'ID': c.cariID, 'Ad Soyad': c.adiSoyadi, 'Firma': c.firmaAdi || '',
                'Türü': c.turu || '', 'Telefon 1': c.telefon1 || '', 'Telefon 2': c.telefon2 || '',
                'E-Posta': c.email || '', 'T.C. Kimlik': c.tc || '', 'Vergi No': c.vergiNo || '',
                'Vergi Dairesi': c.vd || '', 'Adres': c.adres || '', 'Şehir': c.sehir || '',
                'Kayıt Tarihi': tarihFormat(c.kayitTarihi),
            }));
        } else if (key === 'tedarikciler') {
            // tur=Cari tüm cari/tedarikçi kayıtlarını döndürür (Cariler sayfası da bu endpoint'i kullanır)
            const res = await axiosInstance.get('/api/Cariler', { params: { tur: 'Cari' } });
            rows = (res.data || []).map((c: any) => ({
                'ID': c.cariID, 'Ad Soyad / Firma': c.adiSoyadi || c.firmaAdi || '', 'Firma': c.firmaAdi || '',
                'Türü': c.turu || '', 'Alt Kategori': c.altKategori || '', 'Telefon': c.telefon1 || '',
                'Telefon 2': c.telefon2 || '', 'E-Posta': c.email || '',
                'Vergi No': c.vergiNo || '', 'Vergi Dairesi': c.vd || '',
                'Adres': c.adres || '', 'Şehir': c.sehir || '',
                'Kayıt Tarihi': tarihFormat(c.kayitTarihi),
            }));
        } else if (key === 'daireler') {
            const res = await axiosInstance.get('/api/Daireler');
            rows = (res.data || []).map((d: any) => ({
                'ID': d.daireID, 'Proje': d.blok?.proje?.projeAdi || '', 'Blok': d.blok?.blokAdi || '',
                'Daire No': d.daireNo, 'Kat': d.kat || '', 'Tip': d.tip || '',
                'Net m²': d.netMetrekare || '', 'Brüt m²': d.brutMetrekare || '',
                'Cephe': d.cephe?.cepheAdi || '', 'Manzara': d.manzara?.manzaraAdi || '',
                'Durum': d.durum || '', 'Fiyat': d.fiyat || '', 'Para Birimi': d.paraBirimi || 'TL',
            }));
        } else if (key === 'sozlesmeler') {
            const res = await axiosInstance.get('/api/Sozlesmeler');
            rows = (res.data || []).map((s: any) => ({
                'Takip No': s.sozlesmeTakipNo, 'Müşteri': s.cari?.adiSoyadi || '',
                'Proje': s.daire?.blok?.proje?.projeAdi || '', 'Blok': s.daire?.blok?.blokAdi || '',
                'Daire No': s.daire?.daireNo || '', 'Sözleşme Tarihi': tarihFormat(s.sozlesmeTarihi),
                'Sözleşme Fiyatı': s.sozlesmeFiyati, 'Durum': s.sozlesmeDurumu || '',
                'Ödenen': s.odenenTutar || 0, 'Kalan': s.kalanTutar || 0,
            }));
        } else if (key === 'odemeler') {
            const res = await axiosInstance.get('/api/OdemePlanTaksitleri');
            rows = (res.data || []).map((t: any) => ({
                'Taksit ID': t.taksitID,
                'Sözleşme Takip No': t.sozlesmeTakipNo || '',
                'Müşteri': t.musteriAdi || t.cariAdi || '',
                'Proje': t.projeAdi || '',
                'Blok': t.blokAdi || '',
                'Daire No': t.daireNo || '',
                'Vade Tarihi': tarihFormat(t.vadeTarihi),
                'Tahsilat Tarihi': tarihFormat(t.tahsilatTarihi),
                'Miktar': t.miktar,
                'Durum': t.odemeDurumu || '',
                'Ödeme Türü': t.odemeTuruDetay || t.odemeTuru || '',
                'Açıklama': t.aciklama || '',
            }));
        } else if (key === 'potansiyeller') {
            const res = await axiosInstance.get('/api/Leads');
            rows = (res.data || []).map((l: any) => ({
                'ID': l.leadID, 'Ad Soyad': l.adSoyad || '', 'Telefon': l.telefon || '',
                'E-Posta': l.email || '', 'Kaynak': l.kaynak || '', 'Durum': l.durum || '',
                'İlgi Durumu': l.ilgiDurumu || '', 'Atanan Personel': l.atananPersonelAdi || '',
                'Kayıt Tarihi': tarihFormat(l.kayitTarihi), 'Son İşlem': tarihFormat(l.sonIslemTarihi),
                'Notlar': l.notlar || '',
            }));
        } else if (key === 'musteriNotlari') {
            const res = await axiosInstance.get('/api/Notlar');
            rows = (res.data || []).map((n: any) => ({
                'Not ID': n.notID, 'Müşteri': n.cariAdi || n.ilgiliAd || '',
                'Takip No': n.takipNo || '', 'Not Türü': n.notTuru || '',
                'İçerik': n.icerik || n.notMetni || '', 'Oluşturan': n.olusturanAdi || '',
                'Tarih': tarihFormat(n.olusturmaTarihi || n.kayitTarihi),
            }));
        } else if (key === 'personel') {
            const res = await axiosInstance.get('/api/Kullanicilar');
            rows = (res.data || []).map((k: any) => ({
                'ID': k.kullaniciID, 'Ad Soyad': k.adSoyad || '', 'E-Posta': k.email || '',
                'Kullanıcı Adı': k.kullaniciAdi || '', 'Rol': k.kullaniciRolu || '',
                'Aktif': k.aktifMi ? 'Evet' : 'Hayır', 'Kayıt Tarihi': tarihFormat(k.kayitTarihi),
            }));
        }

        const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ 'Bilgi': 'Kayıt bulunamadı' }]);
        if (rows.length && Object.keys(rows[0]).length > 0) {
            const cols = Object.keys(rows[0]);
            ws['!cols'] = cols.map(() => ({ wch: 22 }));
        }
        XLSX.utils.book_append_sheet(wb, ws, secenek.label.substring(0, 31));
    };

    // ── Toplu PDF ────────────────────────────────────────────────────────────

    const toggleProje = (id: number) => {
        setSeciliProjeler(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const tumProjeleriSec = () => {
        if (seciliProjeler.size === projeler.length) setSeciliProjeler(new Set());
        else setSeciliProjeler(new Set(projeler.map(p => p.projeID)));
    };

    const topluPdfIndir = async () => {
        if (seciliProjeler.size === 0) { mesajGoster(setPdfMesaj, 'hata', 'En az bir proje seçin.'); return; }
        setPdfYukleniyor(true);
        try {
            // 1. Tüm sözleşmeleri çek
            const sozRes = await axiosInstance.get('/api/Sozlesmeler');
            const tumSozlesmeler: Sozlesme[] = sozRes.data || [];
            const seciliSozlesmeler = tumSozlesmeler.filter(s =>
                seciliProjeler.has(s.daire?.blok?.proje?.projeID ?? -1)
            );
            if (seciliSozlesmeler.length === 0) {
                mesajGoster(setPdfMesaj, 'hata', 'Seçili projelerde sözleşme bulunamadı.'); return;
            }
            // 2. Her sözleşme için taksitleri paralel çek
            const taksitMap = new Map<number, Taksit[]>();
            await Promise.all(
                seciliSozlesmeler.map(async s => {
                    try {
                        const r = await axiosInstance.get(`/api/OdemePlanTaksitleri?sozlesmeID=${s.sozlesmeID}`);
                        taksitMap.set(s.sozlesmeID, r.data || []);
                    } catch { taksitMap.set(s.sozlesmeID, []); }
                })
            );
            // 3. Kurumsal bilgi
            let kurumsal: any = {};
            try { const kr = await axiosInstance.get('/api/KurumsalBilgiler'); kurumsal = kr.data || {}; } catch {}

            // 4. Print window aç
            const win = window.open('', '_blank', 'width=900,height=700');
            if (!win) { mesajGoster(setPdfMesaj, 'hata', 'Tarayıcı popup\'u engelledi. Lütfen izin verin.'); return; }

            const html = buildTopluPdfHtml(seciliSozlesmeler, taksitMap, kurumsal);
            win.document.write(html);
            win.document.close();
            win.focus();
            setTimeout(() => { win.print(); }, 800);
            mesajGoster(setPdfMesaj, 'ok', `${seciliSozlesmeler.length} sözleşme yazdırma ekranına gönderildi.`);
        } catch {
            mesajGoster(setPdfMesaj, 'hata', 'PDF oluşturulurken hata oluştu.');
        } finally {
            setPdfYukleniyor(false);
        }
    };

    const buildTopluPdfHtml = (
        sozlesmeler: Sozlesme[],
        taksitMap: Map<number, Taksit[]>,
        k: any,
    ): string => {
        const sozlesmePages = sozlesmeler.map(s => {
            const taksitler = (taksitMap.get(s.sozlesmeID) || [])
                .filter(t => t.odemeDurumu !== 'Iptal')
                .sort((a, b) => (a.vadeTarihi || '') < (b.vadeTarihi || '') ? -1 : 1);
            const odenenToplam = taksitler.filter(t => t.odemeDurumu === 'Odendi').reduce((s, t) => s + (t.miktar ?? 0), 0);
            const bekleyenToplam = taksitler.filter(t => t.odemeDurumu === 'Bekliyor').reduce((s, t) => s + (t.miktar ?? 0), 0);

            const taksitRows = taksitler.map((t, i) => `
                <tr>
                    <td>${i + 1}</td>
                    <td>${tarihFormat(t.vadeTarihi)}</td>
                    <td style="text-align:right;font-weight:600">${paraFormat(t.miktar)} ₺</td>
                    <td>${t.odemeTuru || '—'}</td>
                    <td style="color:${t.odemeDurumu === 'Odendi' ? '#15803d' : '#a16207'};font-weight:600">${t.odemeDurumu === 'Odendi' ? '✓ Ödendi' : '○ Bekliyor'}</td>
                    <td style="color:#6b7280;font-size:9pt">${tarihFormat(t.tahsilatTarihi) || '—'}</td>
                    <td style="color:#6b7280;font-size:9pt">${t.aciklama || '—'}</td>
                </tr>
            `).join('');

            return `
            <div class="sayfa">
                <div class="baslik-bar">
                    <div>
                        <div class="baslik-title">SATIŞ SÖZLEŞMESİ</div>
                        ${k?.firmaAdi ? `<div class="firma-adi">${k.firmaAdi}</div>` : ''}
                    </div>
                    <div class="takip-box">
                        <div class="takip-label">Takip No</div>
                        <div class="takip-no">${s.sozlesmeTakipNo}</div>
                        <div class="takip-label" style="margin-top:6px">Tarih</div>
                        <div style="font-size:10pt;font-weight:600">${tarihFormat(s.sozlesmeTarihi)}</div>
                    </div>
                </div>

                <div class="fiyat-bar">
                    <div>
                        <div style="font-size:8pt;opacity:0.7;margin-bottom:2px">Sözleşme Fiyatı</div>
                        <div style="font-size:16pt;font-weight:900;color:#cd6906">${paraFormat(s.sozlesmeFiyati)} ₺</div>
                    </div>
                    <div style="width:1px;background:rgba(255,255,255,0.2);height:36px"></div>
                    <div>
                        <div style="font-size:8pt;opacity:0.7;margin-bottom:2px">Sözleşme Tarihi</div>
                        <div style="font-size:12pt;font-weight:700">${tarihFormat(s.sozlesmeTarihi)}</div>
                    </div>
                    <div style="width:1px;background:rgba(255,255,255,0.2);height:36px"></div>
                    <div>
                        <div style="font-size:8pt;opacity:0.7;margin-bottom:2px">Proje</div>
                        <div style="font-size:11pt;font-weight:700">${s.daire?.blok?.proje?.projeAdi || '—'}</div>
                    </div>
                </div>

                <div class="taraflar">
                    <div class="taraf-kart">
                        <div class="taraf-baslik">SATICI BİLGİLERİ</div>
                        ${k?.firmaAdi ? `<div class="taraf-satir"><span>Firma</span><span>${k.firmaAdi}</span></div>` : ''}
                        ${k?.adres ? `<div class="taraf-satir"><span>Adres</span><span>${k.adres}${k.sehir ? ' / ' + k.sehir : ''}</span></div>` : ''}
                        ${k?.vd ? `<div class="taraf-satir"><span>Vergi Dairesi</span><span>${k.vd}</span></div>` : ''}
                        ${k?.vn ? `<div class="taraf-satir"><span>Vergi No</span><span>${k.vn}</span></div>` : ''}
                    </div>
                    <div class="taraf-kart">
                        <div class="taraf-baslik">ALICI BİLGİLERİ</div>
                        <div class="taraf-satir"><span>Ad Soyad</span><span>${s.cari?.adiSoyadi || '—'}</span></div>
                        ${s.cari?.tc ? `<div class="taraf-satir"><span>T.C. Kimlik</span><span>${s.cari.tc}</span></div>` : ''}
                        ${s.cari?.telefon1 ? `<div class="taraf-satir"><span>Telefon</span><span>${s.cari.telefon1}</span></div>` : ''}
                        ${s.cari?.email ? `<div class="taraf-satir"><span>E-Posta</span><span>${s.cari.email}</span></div>` : ''}
                        ${s.cari?.adres ? `<div class="taraf-satir"><span>Adres</span><span>${s.cari.adres}</span></div>` : ''}
                    </div>
                    <div class="taraf-kart">
                        <div class="taraf-baslik">DAİRE BİLGİLERİ</div>
                        <div class="taraf-satir"><span>Proje</span><span>${s.daire?.blok?.proje?.projeAdi || '—'}</span></div>
                        <div class="taraf-satir"><span>Blok / Daire</span><span>${s.daire?.blok?.blokAdi || ''} — ${s.daire?.daireNo || '—'}</span></div>
                        ${s.daire?.tip ? `<div class="taraf-satir"><span>Tip</span><span>${s.daire.tip}</span></div>` : ''}
                        ${s.daire?.netMetrekare ? `<div class="taraf-satir"><span>Net / Brüt</span><span>${s.daire.netMetrekare} m² / ${s.daire.brutMetrekare || '?'} m²</span></div>` : ''}
                        ${s.daire?.kat !== undefined ? `<div class="taraf-satir"><span>Kat</span><span>${s.daire.kat}. Kat</span></div>` : ''}
                    </div>
                </div>

                ${taksitler.length > 0 ? `
                <div class="tablo-baslik">ÖDEME PLANI</div>
                <table class="odeme-tablo">
                    <thead>
                        <tr>
                            <th>#</th><th>Vade Tarihi</th><th style="text-align:right">Miktar</th>
                            <th>Ödeme Türü</th><th>Durum</th><th>Tahsilat Tarihi</th><th>Açıklama</th>
                        </tr>
                    </thead>
                    <tbody>${taksitRows}</tbody>
                    <tfoot>
                        <tr class="toplam-satir-odendi">
                            <td colspan="2">ÖDENDİ TOPLAM</td>
                            <td style="text-align:right;font-weight:800">${paraFormat(odenenToplam)} ₺</td>
                            <td colspan="4"></td>
                        </tr>
                        <tr class="toplam-satir-bekliyor">
                            <td colspan="2">BEKLİYOR TOPLAM</td>
                            <td style="text-align:right;font-weight:800">${paraFormat(bekleyenToplam)} ₺</td>
                            <td colspan="4"></td>
                        </tr>
                    </tfoot>
                </table>` : '<div style="color:#94a3b8;font-size:10pt;padding:12px 0">Ödeme planı bulunmuyor.</div>'}

                <div class="imza-alani">
                    <div class="imza-kutu">
                        <div class="imza-baslik">SATICI</div>
                        <div class="imza-ad">${k?.firmaAdi || ''}</div>
                        <div class="imza-cizgi"></div>
                        <div class="imza-alt">İmza &nbsp;&nbsp;&nbsp; Tarih: _____ / _____ / _________</div>
                    </div>
                    <div class="imza-kutu">
                        <div class="imza-baslik">ALICI</div>
                        <div class="imza-ad">${s.cari?.adiSoyadi || ''}</div>
                        <div class="imza-cizgi"></div>
                        <div class="imza-alt">İmza &nbsp;&nbsp;&nbsp; Tarih: _____ / _____ / _________</div>
                    </div>
                </div>
            </div>`;
        }).join('');

        return `<!DOCTYPE html><html lang="tr"><head>
        <meta charset="UTF-8">
        <title>Toplu Sözleşme PDF</title>
        <style>
            @page { size: A4; margin: 10mm; }
            @media print {
                body * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                .sayfa { page-break-after: always; }
                .sayfa:last-child { page-break-after: avoid; }
            }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 10pt; color: #111827; background: #f3f4f6; }
            .sayfa { background: #fff; padding: 12mm 10mm; max-width: 190mm; margin: 0 auto 16px; }
            .baslik-bar { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 10px; border-bottom: 2.5px solid #111827; margin-bottom: 12px; }
            .baslik-title { font-size: 18pt; font-weight: 900; color: #111827; text-transform: uppercase; letter-spacing: -0.5px; }
            .firma-adi { font-size: 9pt; color: #6b7280; margin-top: 4px; }
            .takip-box { background: #111827; color: #fff; border-radius: 8px; padding: 8px 14px; text-align: right; min-width: 140px; }
            .takip-label { font-size: 8pt; opacity: 0.7; margin-bottom: 2px; }
            .takip-no { font-size: 11pt; font-weight: 800; letter-spacing: 0.5px; }
            .fiyat-bar { background: #111827; color: #fff; border-radius: 8px; padding: 10px 16px; display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; gap: 16px; }
            .taraflar { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; margin-bottom: 12px; }
            .taraf-kart { background: #f8fafc; border-left: 3px solid #111827; border-radius: 0 6px 6px 0; padding: 8px 10px; }
            .taraf-baslik { font-size: 7.5pt; font-weight: 800; text-transform: uppercase; letter-spacing: 0.8px; color: #111827; margin-bottom: 6px; }
            .taraf-satir { display: flex; gap: 6px; margin-bottom: 3px; font-size: 8.5pt; }
            .taraf-satir span:first-child { font-weight: 700; color: #4b5563; min-width: 70px; flex-shrink: 0; }
            .tablo-baslik { font-size: 8pt; font-weight: 800; text-transform: uppercase; letter-spacing: 0.8px; color: #111827; margin-bottom: 4px; }
            .odeme-tablo { width: 100%; border-collapse: collapse; font-size: 8.5pt; margin-bottom: 10px; }
            .odeme-tablo thead tr { background: #f3f4f6; }
            .odeme-tablo th { padding: 5px 6px; text-align: left; font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: #6b7280; border-bottom: 1.5px solid #e5e7eb; }
            .odeme-tablo td { padding: 4px 6px; border-bottom: 1px solid #f3f4f6; }
            .odeme-tablo tbody tr:nth-child(even) { background: #fafafa; }
            .toplam-satir-odendi td { background: #f0fdf4; color: #15803d; padding: 4px 6px; font-size: 8pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
            .toplam-satir-bekliyor td { background: #fefce8; color: #a16207; padding: 4px 6px; font-size: 8pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
            .imza-alani { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 20px; padding-top: 16px; border-top: 1px solid #e5e7eb; }
            .imza-baslik { font-size: 7.5pt; font-weight: 800; text-transform: uppercase; letter-spacing: 0.8px; color: #111827; margin-bottom: 4px; }
            .imza-ad { font-size: 9pt; font-weight: 600; color: #374151; margin-bottom: 10px; }
            .imza-cizgi { height: 36px; border-bottom: 1.5px dashed #cbd5e1; margin-bottom: 6px; }
            .imza-alt { font-size: 7.5pt; color: #9ca3af; }
        </style>
        </head><body>${sozlesmePages}</body></html>`;
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    if (user?.kullaniciRolu !== 'Admin') {
        return (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
                <i className="fas fa-lock" style={{ fontSize: 40, marginBottom: 16 }} />
                <div style={{ fontSize: 15, fontWeight: 600 }}>Bu sayfaya erişim yetkiniz bulunmuyor.</div>
                <div style={{ fontSize: 13, marginTop: 6 }}>Yalnızca sistem yöneticileri bu sayfayı görüntüleyebilir.</div>
            </div>
        );
    }

    const seciliSayisi = secili.size;
    const tumSecili = seciliSayisi === EXCEL_SECENEK_LIST.length;

    return (
        <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif", maxWidth: 860 }}>

            {/* ── Excel Bölümü ── */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginBottom: 24 }}>
                {/* Başlık */}
                <div style={{ padding: '18px 22px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className="fas fa-file-excel" style={{ fontSize: 18, color: '#16a34a' }} />
                    </div>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Excel Dışa Aktarım</div>
                        <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>Seçilen kategoriler ayrı Excel sayfaları olarak indirilir</div>
                    </div>
                    {excelMesaj && (
                        <div style={{ marginLeft: 'auto', padding: '7px 14px', background: excelMesaj.tip === 'ok' ? '#f0fdf4' : '#fef2f2', color: excelMesaj.tip === 'ok' ? '#166534' : '#dc2626', border: `1px solid ${excelMesaj.tip === 'ok' ? '#86efac' : '#fecaca'}`, borderRadius: 8, fontSize: 12.5, fontWeight: 600 }}>
                            <i className={`fas fa-${excelMesaj.tip === 'ok' ? 'check-circle' : 'exclamation-circle'} me-2`} />
                            {excelMesaj.mesaj}
                        </div>
                    )}
                </div>

                {/* Seçim listesi */}
                <div style={{ padding: '16px 22px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: '#374151' }}>
                            Aktarılacak Kategoriler
                            {seciliSayisi > 0 && <span style={{ marginLeft: 8, background: '#cd6906', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11 }}>{seciliSayisi} seçili</span>}
                        </div>
                        <button onClick={tumunuSec}
                            style={{ fontSize: 12, fontWeight: 600, color: '#cd6906', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}>
                            {tumSecili ? 'Seçimi Temizle' : 'Tümünü Seç'}
                        </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 10 }}>
                        {EXCEL_SECENEK_LIST.map(s => {
                            const isSecili = secili.has(s.key);
                            return (
                                <div key={s.key} onClick={() => toggleSecim(s.key)}
                                    style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '11px 14px', border: `1.5px solid ${isSecili ? '#cd6906' : '#e5e7eb'}`, borderRadius: 9, background: isSecili ? '#fff7ed' : '#fafafa', cursor: 'pointer', transition: 'all 0.12s' }}>
                                    <div style={{ width: 32, height: 32, borderRadius: 8, background: isSecili ? 'rgba(205,105,6,0.12)' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <i className={s.icon} style={{ fontSize: 14, color: isSecili ? '#cd6906' : '#64748b' }} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: isSecili ? '#cd6906' : '#111827', marginBottom: 2 }}>{s.label}</div>
                                        <div style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.3 }}>{s.aciklama}</div>
                                    </div>
                                    <div style={{ width: 18, height: 18, borderRadius: 5, border: `2px solid ${isSecili ? '#cd6906' : '#d1d5db'}`, background: isSecili ? '#cd6906' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                                        {isSecili && <i className="fas fa-check" style={{ fontSize: 9, color: '#fff' }} />}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Alt buton */}
                <div style={{ padding: '14px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
                    <span style={{ fontSize: 12.5, color: '#94a3b8' }}>
                        {seciliSayisi === 0 ? 'Kategori seçilmedi' : `${seciliSayisi} kategori seçildi — her biri ayrı bir Excel sayfası olur`}
                    </span>
                    <button onClick={excelIndir} disabled={excelYukleniyor || seciliSayisi === 0}
                        style={{ padding: '9px 22px', background: seciliSayisi === 0 ? '#e5e7eb' : '#16a34a', color: seciliSayisi === 0 ? '#94a3b8' : '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: seciliSayisi === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.15s' }}>
                        {excelYukleniyor
                            ? <><i className="fas fa-spinner fa-spin" /> Hazırlanıyor...</>
                            : <><i className="fas fa-file-excel" /> Excel İndir</>}
                    </button>
                </div>
            </div>

            {/* ── Toplu Sözleşme PDF Bölümü ── */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                {/* Başlık */}
                <div style={{ padding: '18px 22px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className="fas fa-file-pdf" style={{ fontSize: 18, color: '#dc2626' }} />
                    </div>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Toplu Sözleşme PDF</div>
                        <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>Seçilen projelerin tüm sözleşmeleri Hazır Sözleşme formatında PDF olarak yazdırılır</div>
                    </div>
                    {pdfMesaj && (
                        <div style={{ marginLeft: 'auto', padding: '7px 14px', background: pdfMesaj.tip === 'ok' ? '#f0fdf4' : '#fef2f2', color: pdfMesaj.tip === 'ok' ? '#166534' : '#dc2626', border: `1px solid ${pdfMesaj.tip === 'ok' ? '#86efac' : '#fecaca'}`, borderRadius: 8, fontSize: 12.5, fontWeight: 600 }}>
                            <i className={`fas fa-${pdfMesaj.tip === 'ok' ? 'check-circle' : 'exclamation-circle'} me-2`} />
                            {pdfMesaj.mesaj}
                        </div>
                    )}
                </div>

                {/* Proje seçimi */}
                <div style={{ padding: '16px 22px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: '#374151' }}>
                            Proje Seçin
                            {seciliProjeler.size > 0 && <span style={{ marginLeft: 8, background: '#dc2626', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11 }}>{seciliProjeler.size} proje seçili</span>}
                        </div>
                        {projeler.length > 0 && (
                            <button onClick={tumProjeleriSec}
                                style={{ fontSize: 12, fontWeight: 600, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}>
                                {seciliProjeler.size === projeler.length ? 'Seçimi Temizle' : 'Tümünü Seç'}
                            </button>
                        )}
                    </div>

                    {projelerYukleniyor ? (
                        <div style={{ textAlign: 'center', padding: '24px', color: '#94a3b8', fontSize: 13 }}>
                            <i className="fas fa-spinner fa-spin me-2" /> Projeler yükleniyor...
                        </div>
                    ) : projeler.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '24px', color: '#94a3b8', fontSize: 13 }}>Proje bulunamadı.</div>
                    ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                            {projeler.map(p => {
                                const isSecili = seciliProjeler.has(p.projeID);
                                return (
                                    <div key={p.projeID} onClick={() => toggleProje(p.projeID)}
                                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', border: `1.5px solid ${isSecili ? '#dc2626' : '#e5e7eb'}`, borderRadius: 8, background: isSecili ? '#fef2f2' : '#fafafa', cursor: 'pointer', transition: 'all 0.12s', fontSize: 13, fontWeight: 600, color: isSecili ? '#dc2626' : '#374151' }}>
                                        <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${isSecili ? '#dc2626' : '#d1d5db'}`, background: isSecili ? '#dc2626' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            {isSecili && <i className="fas fa-check" style={{ fontSize: 8, color: '#fff' }} />}
                                        </div>
                                        <i className="fas fa-building" style={{ fontSize: 12, opacity: 0.7 }} />
                                        {p.projeAdi}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div style={{ marginTop: 14, padding: 12, background: '#f8fafc', borderRadius: 8, fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <i className="fas fa-info-circle" style={{ marginTop: 1, color: '#94a3b8' }} />
                        <div>
                            Seçili projelerin tüm sözleşmeleri alıcı bilgileri, daire bilgileri ve ödeme planı ile birlikte her biri ayrı sayfa olacak şekilde yazdırma/PDF penceresine gönderilir.
                            Tarayıcınızın <strong>yazdır → PDF olarak kaydet</strong> seçeneğini kullanın.
                        </div>
                    </div>
                </div>

                {/* Alt buton */}
                <div style={{ padding: '14px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
                    <span style={{ fontSize: 12.5, color: '#94a3b8' }}>
                        {seciliProjeler.size === 0 ? 'Proje seçilmedi' : `${seciliProjeler.size} proje — tüm sözleşmeleri indirilecek`}
                    </span>
                    <button onClick={topluPdfIndir} disabled={pdfYukleniyor || seciliProjeler.size === 0}
                        style={{ padding: '9px 22px', background: seciliProjeler.size === 0 ? '#e5e7eb' : '#dc2626', color: seciliProjeler.size === 0 ? '#94a3b8' : '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: seciliProjeler.size === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.15s' }}>
                        {pdfYukleniyor
                            ? <><i className="fas fa-spinner fa-spin" /> Hazırlanıyor...</>
                            : <><i className="fas fa-file-pdf" /> Sözleşmeleri İndir</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DisaAktar;
